import {
    asyncListWithPaginationCursorCreate,
    AsyncListWithPaginationCursor,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoStoreProduct, DoppeDtoStoreProductType} from '../../../client-server-common/types/doppe-dto-store-product';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

// Per-page size for browsing the All Products list. Aligned with the products
// list page renderer's `autoFetchCount={20}` so each scroll-to-bottom maps to
// exactly one server fetch (instead of over-fetching once and then stalling on
// scroll — which masks pagination bugs because the in-memory pool already
// contains the whole catalogue for small stores).
const PRODUCTS_LIST_PAGE_SIZE = 20;

export function doppeViewerAsyncListFactoryProducts(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryProducts.Params>({
            search: hashFunctionCreate(),
            productType: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            // Use the dedicated cursor-paginated `getStoreProductsPage` endpoint
            // (NOT `getStoreProducts`, which is skip/take-only and exists for the
            // single-page id/recent-products call sites). Wix Stores V3 only
            // supports cursor pagination natively; the server emulates a cursor
            // for V1 catalogs so this single client path works for both versions.
            return asyncListWithPaginationCursorCreate<DoppeDtoStoreProduct, string>({
                fetchFunction: async (fetchContext: AsyncListWithPaginationCursor.FetchContext<string>) => {
                    const result = await viewerApiClient.getStoreProductsPage({
                        cursor: fetchContext.isInitialFetch ? undefined : fetchContext.cursor,
                        limit: PRODUCTS_LIST_PAGE_SIZE,
                        search: params.search,
                        productType: params.productType,
                    });

                    return {
                        results: result.results,
                        cursor: result.cursor,
                        totalCount: result.totalCount,
                    };
                },
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryProducts {
    export interface Params {
        search?: string;
        productType?: DoppeDtoStoreProductType;
    }
}

export function doppeViewerAsyncListFactoryProductsByCategory(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryProductsByCategory.Params>({
            categorySlug: hashFunctionCreate(),
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithPaginationCursorCreate<DoppeDtoStoreProduct, string>({
                fetchFunction: async (fetchContext: AsyncListWithPaginationCursor.FetchContext<string>) => {
                    const result = await viewerApiClient.getStoreProductsByCategorySlug({
                        categorySlug: params.categorySlug,
                        cursor: fetchContext.isInitialFetch ? undefined : fetchContext.cursor,
                        limit: 30,
                    });

                    return {
                        results: result.results,
                        cursor: result.cursor,
                        totalCount: result.totalCount,
                    };
                },
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryProductsByCategory {
    export interface Params {
        categorySlug: string;
    }
}
